import { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Fa,
  Toggle,
  Table,
  showErrorMessage,
  showInfoMessage,
  showSuccessMessage,
  Loader,
  triggerPrompt,
  TextInput,
  Checkbox,
} from '@eltoro-ui/components'
import {
  V1Pixel,
  V1Subscription,
  V1WebToHomeNoteType,
  V1PixelNote,
} from 'next-gen-sdk'
import { Link, useParams } from 'react-router-dom'
import {
  Loading,
  EmptyField,
  PageContent,
  PixelNotes,
  PixelSubscriptionCreate,
  TablePagination,
  TextInputWithErrorText,
  PixelTagInput,
  PixelSubscriptionHistory,
  PixelFormItem,
} from 'Components'
import { useAppContext } from 'Contexts'
import { listOrgSubscriptions } from '../../Requests'
import { WebToHomeDisabled } from 'Pages/PixelLibraryPage'
import { useTokenPagination } from '@eltoro-ui/hooks'
import { capitalize, formatDate } from 'Helpers'
import { useValidatedState } from '@eltoro-ui/hooks'
import dayjs from 'dayjs'
import classNames from 'classnames'
import { useAuth } from 'react-oidc-context'
import { PixelRejectionModal } from './PixelRejectionModal'

export interface PixelType {
  name: string
  status: 'approved' | 'rejected'
  dateCreated: string
  url: string
  tags: string[]
  isSensitive: boolean
  isLocked: boolean
  notes: string[]
  code: string
  history: PixelHistoryEventType[]
}

export interface PixelHistoryEventType {
  user: string
  timestamp: string
  action: string
}

export interface SubscriptionType {
  name: string
  type: string
  dateCreated: string
  id: string
}

export const PixelViewPage = () => {
  const { pixelId } = useParams<{
    pixelId?: string
  }>()
  const [pixel, setPixel] = useState<V1Pixel>()
  const [isSensitive, setIsSensitive] = useState(false)
  const [isLocked, setIsLocked] = useState(false)
  const [isHidden, setIsHidden] = useState(false)
  const [url, setUrl, isValidUrl] = useValidatedState('https://', 'url')
  const [tags, setTags] = useState<string[]>([])
  const [showAddNotes, setShowAddNotes] = useState(false)
  const [notes, setNotes] = useState<{
    content: string
    type: V1WebToHomeNoteType
  }>()
  const [editUrl, setEditUrl] = useState(false)
  const [createSubscription, setCreateSubscription] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refresh, setRefresh] = useState<string>('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [noteEditing, setNoteEditing] = useState<V1PixelNote>()
  const auth = useAuth()
  const user = auth.user?.profile
  const {
    webToHomeApi,
    currentOrg,
    isAdmin,
    webToHomeEnabled,
    isReadOnly,
    isUser,
  } = useAppContext()

  const query = currentOrg?.id ? `?org_id=${currentOrg.id}` : ''

  useEffect(() => {
    if (pixelId && currentOrg) {
      webToHomeApi
        ?.advertisingPlatformServiceGetPixel(pixelId, currentOrg?.id)
        .then((res) => {
          setPixel(res)
          setUrl(res?.topLevelUrl || '')
          setTags(res?.tags || [])
          setIsSensitive(res?.sensitive || false)
          setIsLocked(res?.locked || false)
          setIsHidden(res?.hidden || false)
        })
    }
  }, [])

  const handleFetchSubscriptions = useCallback(
    async ({
      pageSize,
      nextPageToken,
    }: {
      pageSize: number
      nextPageToken?: string
    }) => {
      const empty = {
        data: [],
        totalItems: 0,
      }

      if (!currentOrg?.id) return empty
      try {
        const res = await listOrgSubscriptions(
          pageSize,
          nextPageToken,
          currentOrg.id,
          'created desc', // order by
          undefined, // id
          undefined, // name
          pixelId, // pixel id
        )
        if (res) {
          return {
            data: res.subscriptions || [],
            totalItems: res.totalSize || 0,
            nextPageToken: res.nextPageToken,
          }
        }
      } catch {
        showErrorMessage('Error fetching your subscriptions', '')
      }

      return empty
    },
    [currentOrg?.id, pixelId],
  )

  const pagination = useTokenPagination<V1Subscription>(
    {
      pageSize: 10,
      fetchData: handleFetchSubscriptions,
    },
    [currentOrg, refresh],
  )

  const handleCopyCode = () => {
    navigator.clipboard
      .writeText(pixel?.html || '')
      .then(() => showSuccessMessage('Pixel code has been copied', ''))
      .catch((err) => showErrorMessage('Failed to copy code:', err))
  }
  const handleUpdatePixelField = (
    field: 'sensitive' | 'locked' | 'hidden',
    value: boolean,
    setState: React.Dispatch<React.SetStateAction<boolean>>,
    errorMessage: string,
  ) => {
    if (!webToHomeApi || !pixelId) return
    setState(value)
    setSaving(true)
    showInfoMessage('Updating pixel...', '')
    webToHomeApi
      .advertisingPlatformServiceUpdatePixel(
        pixelId,
        { [field]: value },
        field,
        currentOrg?.id,
      )
      .then((res) => {
        setPixel(res)
        showSuccessMessage('Pixel updated', '')
      })
      .catch(() => {
        showErrorMessage(errorMessage, '')
        setState(!value)
      })
      .finally(() => setSaving(false))
  }

  const handleSetSensitiveIndustry = (value: boolean) => {
    handleUpdatePixelField(
      'sensitive',
      value,
      setIsSensitive,
      'Error updating Sensitive Industry',
    )
  }

  const handleSetLocked = (value: boolean) => {
    handleUpdatePixelField(
      'locked',
      value,
      setIsLocked,
      'Error updating pixel locked field',
    )
  }

  const handleSetHidden = (value: boolean) => {
    handleUpdatePixelField(
      'hidden',
      value,
      setIsHidden,
      'Error updating pixel hidden field',
    )
  }

  const handleSaveTopLevelUrl = () => {
    if (
      !webToHomeApi ||
      !pixelId ||
      saving ||
      (editUrl && !isValidUrl) ||
      pixel?.locked ||
      isUser
    )
      return
    setSaving(true)
    showInfoMessage('Updating pixel...', '')
    webToHomeApi
      .advertisingPlatformServiceUpdatePixel(
        pixelId,
        { topLevelUrl: url },
        'top_level_url',
        currentOrg?.id,
      )
      .then((res) => {
        setPixel(res)
        showSuccessMessage('Pixel updated', '')
      })
      .catch(() => {
        showErrorMessage('Error updating top level URL', '')
      })
      .finally(() => setSaving(false))
  }

  const handleUpdateTags = (tags: string[]) => {
    if (!webToHomeApi || !pixelId) return
    setTags(tags)
    setSaving(true)
    showInfoMessage('Updating pixel...', '')
    webToHomeApi
      .advertisingPlatformServiceUpdatePixel(
        pixelId,
        { tags },
        'tags',
        currentOrg?.id,
      )
      .then((res) => {
        setPixel(res)
        showSuccessMessage('Pixel updated', '')
      })
      .catch(() => {
        setTags(pixel?.tags || [])
        showErrorMessage('Error updating pixel tags', '')
      })
      .finally(() => setSaving(false))
  }

  const handleUpdateStatus = async (
    status: { label: string; value: string },
    rejectionReason?: string,
  ) => {
    if (!webToHomeApi || !pixelId) return
    setSaving(true)
    showInfoMessage('Updating pixel...', '')
    try {
      // update status and locked if rejected
      const updatedPixel = await webToHomeApi.advertisingPlatformServiceUpdatePixel(
        pixelId,
        {
          status: status.value,
          locked: status.value === 'denied' ? true : undefined, // lock if rejected
        },
        'status,locked',
        currentOrg?.id,
      )

      let newNote: V1PixelNote | undefined = undefined
      // create note with rejection reason
      if (status.value === 'denied' && rejectionReason) {
        newNote = await webToHomeApi.advertisingPlatformServiceCreatePixelNote(
          pixelId,
          {
            content: `Rejection Reason: ${rejectionReason}`,
            type: 'NOTE_TYPE_USER', // user type so they can see it
          },
          currentOrg!.id!,
        )
      }

      setPixel({
        ...updatedPixel,
        notes: [...(updatedPixel.notes || []), ...(newNote ? [newNote] : [])],
      })
      if (updatedPixel.locked !== undefined) setIsLocked(updatedPixel.locked)
      showSuccessMessage('Pixel updated', '')
    } catch (err) {
      showErrorMessage('Error updating pixel status', '')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateNote = async (newNote: V1PixelNote) => {
    if (!webToHomeApi || !newNote.id || !pixelId) return
    setSaving(true)
    webToHomeApi
      .advertisingPlatformServiceUpdatePixelNote(
        pixelId,
        newNote.id,
        {
          content: newNote.content || '',
          type: newNote.type,
        },
        undefined,
        pixel?.orgId,
      )
      .then((res) => {
        setPixel((prev) => ({
          ...prev,
          notes:
            prev?.notes?.map((note) => (note.id === res.id ? res : note)) || [],
        }))
        setNoteEditing(undefined)
      })
      .catch(() => showErrorMessage('Error saving pixel note edits', ''))
      .finally(() => setSaving(false))
  }

  const handleSaveNote = () => {
    if (!pixelId || !currentOrg?.id || !notes?.content || !webToHomeApi) return
    setSaving(true)
    webToHomeApi
      .advertisingPlatformServiceCreatePixelNote(
        pixelId,
        {
          content: notes?.content || '',
          type: notes?.type || 'NOTE_TYPE_USER',
        },
        currentOrg!.id!,
      )
      .then((res) => {
        setPixel((prev) => ({
          ...prev,
          notes: [...(prev?.notes || []), res],
        }))
        setShowAddNotes(false)
      })
      .catch(() => showErrorMessage('Error saving pixel note', ''))
      .finally(() => setSaving(false))
  }

  const handleDeleteNote = (noteId: string) => {
    triggerPrompt(
      'Delete note',
      <p>Are you sure you want to delete this note?</p>,
      () => {
        if (!pixelId || !currentOrg?.id || !webToHomeApi) return
        setSaving(true)

        webToHomeApi
          ?.advertisingPlatformServiceDeletePixelNote(
            pixelId,
            noteId,
            currentOrg!.id!,
          )
          .then((res) => {
            setPixel((prev) => ({
              ...prev,
              notes: prev?.notes?.filter((note) => note.id !== noteId),
            }))
          })
          .catch(() => showErrorMessage('Error deleting pixel note', ''))
          .finally(() => setSaving(false))
      },
    )
  }

  const rejectionNote =
    pixel?.status === 'denied' &&
    pixel?.notes?.find(
      (note) =>
        note.type === 'NOTE_TYPE_USER' &&
        note.content?.includes('Rejection Reason:'),
    )

  const canSeePixelCode =
    isAdmin || (isUser && pixel?.status === 'approved' && !pixel.locked)

  if (!isAdmin && !webToHomeEnabled) return <WebToHomeDisabled />

  if (!pixel) return <Loading />

  return (
    <PageContent className="mx-auto max-w-7xl">
      {createSubscription && (
        <PixelSubscriptionCreate
          offClick={() => setCreateSubscription(false)}
          incomingPixel={pixel}
          setRefresh={setRefresh}
        />
      )}
      <div className="flex items-center gap-4 pl-4">
        <Link
          to={`/pixel-library/pixels${query}`}
          className="flex items-center"
        >
          <Fa icon="arrow-left" size={1} className="mr-2 h-5 w-5" />
          Back to Pixel Library
        </Link>
      </div>
      <div
        className="Box--flex-direction-column items-between mx-6 mt-6 flex justify-between gap-4 rounded-lg p-4 shadow-sm transition-colors"
        style={{
          background: 'var(--highlight-bg)',
          borderColor: 'var(--color-grey-200)',
          color: 'var(--base-text)',
        }}
      >
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="laptopsmall:flex-wrap flex items-center gap-2">
            <h1
              className="flex items-center gap-2 break-all text-xl font-semibold"
              style={{ color: 'var(--base-text)' }}
            >
              {pixel?.name}
              {isSensitive && <Fa icon="warning" size={1} />}
              {isLocked && <Fa icon="lock" size={1} />}
              {isHidden && <Fa icon="user-secret" size={1} />}
            </h1>
            <div
              className={classNames(
                'max-w-min rounded-full py-1 px-3 text-xs font-bold',
                {
                  'bg-danger-300 text-current': pixel?.status === 'denied',
                  'text-success-500 bg-success-50':
                    pixel?.status === 'approved',
                  'bg-warning-50 text-warning-500':
                    pixel?.status === 'submitted',
                },
              )}
            >
              {(() => {
                if (pixel?.status === 'approved') return 'Approved'
                if (pixel?.status === 'denied') return 'Rejected'
                return capitalize(pixel?.status || '')
              })()}
            </div>
          </div>
          <div className="Box--flex-direction-row flex items-center justify-end gap-2 self-end">
            {isAdmin && (
              <>
                {(pixel.status === 'denied' ||
                  pixel.status === 'submitted') && (
                  <Button
                    iconLeft={
                      <Fa
                        icon="circle-check"
                        size={1}
                        className={pixel.locked ? '' : '!text-white'}
                      />
                    }
                    onClick={() =>
                      handleUpdateStatus({
                        label: 'Approved',
                        value: 'approved',
                      })
                    }
                    className="!bg-success !border-success hover:!bg-success-700 hover:!border-success-700 rounded-md !text-white"
                    disabled={pixel.locked}
                  >
                    Approve
                  </Button>
                )}
                {pixel.status === 'submitted' && (
                  <Button
                    iconLeft={
                      <Fa
                        icon="circle-xmark"
                        size={1}
                        className={pixel.locked ? '' : '!text-white'}
                      />
                    }
                    onClick={() => setShowRejectModal(true)}
                    className="hover:!bg-danger-700 hover:!border-danger-700 rounded-md !text-white"
                    kind="danger"
                    disabled={pixel.locked}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
            {!isReadOnly && (
              <Button
                onClick={() => setCreateSubscription(true)}
                className="rounded-md py-2"
                kind="primary"
                disabled={
                  pixel.status !== 'approved' || pixel.locked || isLocked
                }
              >
                Create Subscription
              </Button>
            )}
          </div>
        </div>
        {/* Toggles */}
        <div className="flex justify-between gap-4">
          {isSensitive ? (
            <div className="bg-danger-300 rounded-m flex w-[50%] gap-2 p-4 text-current">
              <Fa icon="warning" size={1} className="!text-current" />
              The URL associated with this pixel has been flagged as sensitive.
            </div>
          ) : (
            <div />
          )}
          {isAdmin && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <Toggle
                  isDisabled={isReadOnly || saving || pixel.locked}
                  size="s"
                  isChecked={isSensitive}
                  onChange={handleSetSensitiveIndustry}
                />
                <span className="text-sm">Sensitive Industry</span>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  isDisabled={isReadOnly || saving}
                  size="s"
                  isChecked={isLocked}
                  onChange={handleSetLocked}
                />
                <span className="text-sm">Lock Pixel</span>
              </div>
              <div className="flex items-center gap-2">
                <Toggle
                  size="s"
                  isChecked={isHidden}
                  onChange={handleSetHidden}
                  isDisabled={saving || pixel.locked}
                />
                <span className="text-sm">Hide Pixel</span>
              </div>
            </div>
          )}
        </div>
        {rejectionNote && (
          <div className="bg-danger-300 rounded-m p-4 text-current">
            <strong>Rejection Reason</strong>
            <br />
            <p className="mt-2">
              {rejectionNote.content?.replace('Rejection Reason: ', '')}
            </p>
          </div>
        )}
        {/* Date and URL */}
        <div className="grid grid-cols-2 gap-6">
          <div className="p-4">
            <h2 className="text-sm font-medium">Date Created</h2>
            <p className="mt-1">
              {pixel?.created ? formatDate(pixel.created) : 'N/A'}
            </p>
          </div>
          <div className="flex items-center justify-end p-4">
            <div className="Box--flex-direction-column justify-end">
              <h2 className="text-sm font-medium">
                Pixel Placement Top-Level URL
              </h2>
              <p className="Box--flex-direction-row mt-1 flex items-center">
                {editUrl ? (
                  <TextInputWithErrorText
                    id="topLevelUrl"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    isValid={isValidUrl && url.length <= 1000}
                    baseValues={['http://', 'https://']}
                    maxLength={1000}
                    popoverContent={(() => {
                      return (
                        <div className="text-warning">
                          Please enter a valid url under 1,000 characters:
                          &quot;https://www.websitename.com&quot;
                        </div>
                      )
                    })()}
                  />
                ) : (
                  <span>{pixel?.topLevelUrl || 'N/A'}</span>
                )}
                {!isReadOnly && (
                  <button
                    disabled={
                      saving ||
                      (editUrl && !isValidUrl) ||
                      pixel.locked ||
                      isUser
                    }
                    onClick={() => {
                      setEditUrl(!editUrl)
                      if (editUrl) handleSaveTopLevelUrl()
                    }}
                    type="button"
                    className="text-primary ml-2 transition-opacity hover:opacity-80"
                  >
                    {editUrl ? 'Save' : 'Edit'}
                  </button>
                )}
                {editUrl && (
                  <button
                    onClick={() => {
                      setEditUrl(!editUrl)
                      if (editUrl)
                        if (pixel?.topLevelUrl) setUrl(pixel.topLevelUrl)
                    }}
                    type="button"
                    className="text-primary ml-2 transition-opacity hover:opacity-80"
                  >
                    Cancel
                  </button>
                )}
              </p>
            </div>
          </div>
        </div>
        {/* Tags */}
        <div className="p-4">
          <h2 className="mb-4 text-sm font-medium">Tags</h2>
          <PixelTagInput
            tags={tags}
            setTags={handleUpdateTags}
            disabled={isReadOnly || saving || pixel.locked}
          />
        </div>
        {/* Notes */}
        <div className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-medium">Notes</h2>
            <Button
              onClick={() => setShowAddNotes(!showAddNotes)}
              className="text-center"
              disabled={saving || isReadOnly || pixel.locked}
            >
              {showAddNotes ? 'Close' : 'Add Note'}
            </Button>
          </div>
          {showAddNotes && (
            <div className="animate-slidedown mb-2 flex flex-col py-1">
              <PixelNotes
                type="pixel"
                onChange={(content, type) => setNotes({ content, type })}
                showLabel={false}
                onSaveNote={handleSaveNote}
                saving={saving}
              />
            </div>
          )}
          <div className="PixelViewPage__notes-table mt-2 flex flex-col gap-2">
            {(pixel?.notes || []).length > 0 ? (
              pixel?.notes?.map((currentNote) => {
                const {
                  content,
                  type,
                  userName,
                  updateTime,
                  id,
                  createTime,
                  userId,
                } = currentNote
                const canEditNote =
                  !isReadOnly && (userId === user?.sub || isAdmin)
                if (!isAdmin && type === 'NOTE_TYPE_ADMIN') return null
                return (
                  <div
                    className={classNames(
                      'bg-tint-gray-100 flex items-center justify-between rounded p-2',
                      {
                        'bg-base-info-50': type === 'NOTE_TYPE_ADMIN',
                      },
                    )}
                    key={id}
                  >
                    <div className="w-full p-1 text-sm">
                      {noteEditing?.id === id ? (
                        <div className="flex gap-2">
                          <div className="flex w-full flex-col gap-1">
                            <PixelFormItem
                              label=""
                              htmlFor="pixel-note-edit"
                              valid={(noteEditing?.content || '').length <= 500}
                              counter
                              className="w-full"
                            >
                              <TextInput
                                value={noteEditing?.content || ''}
                                onChange={(e) =>
                                  setNoteEditing((prev) => ({
                                    ...prev,
                                    content: e.target.value,
                                  }))
                                }
                                maxLength={501}
                              />
                            </PixelFormItem>
                            {isAdmin && (
                              <Checkbox
                                checked={
                                  noteEditing?.type === 'NOTE_TYPE_ADMIN'
                                }
                                label="Mark as admin note"
                                onChange={(bool) => {
                                  if (bool) {
                                    setNoteEditing((prev) => ({
                                      ...prev,
                                      type: 'NOTE_TYPE_ADMIN',
                                    }))
                                  } else {
                                    setNoteEditing((prev) => ({
                                      ...prev,
                                      type: 'NOTE_TYPE_USER',
                                    }))
                                  }
                                }}
                              />
                            )}
                          </div>
                          <Button
                            disabled={
                              (noteEditing?.content || '')?.length > 500 ||
                              saving ||
                              (noteEditing?.content || '')?.length < 1
                            }
                            onClick={() =>
                              noteEditing && handleUpdateNote(noteEditing)
                            }
                            className="rounded-md !p-4"
                            iconOnly={<Fa icon="save" size={1} />}
                            kind="primary"
                          />
                          <Button
                            onClick={() => setNoteEditing(undefined)}
                            className="rounded-md !p-4"
                            iconOnly={<Fa icon="xmark" size={1} />}
                          />
                        </div>
                      ) : (
                        <p>{content}</p>
                      )}
                      <p className="text-tint-gray-700 mt-1 text-xs">
                        {userName}{' '}
                        {(() => {
                          if (type === 'NOTE_TYPE_ADMIN')
                            return (
                              <span className="text-base-info font-bold">
                                (Admin)
                              </span>
                            )
                          if (type === 'NOTE_TYPE_PUBLISHER')
                            return (
                              <span className="font-bold">(Publisher)</span>
                            )
                          return null
                        })()}{' '}
                        {userName ? '- ' : ''}
                        {dayjs(createTime).format('MM/DD/YYYY, H:mm:ss A')}
                        {updateTime &&
                          !dayjs(updateTime).isSame(
                            createTime,
                            'millisecond',
                          ) &&
                          ` (edited ${dayjs(updateTime).format(
                            'MM/DD/YYYY, H:mm:ss A',
                          )})`}
                      </p>
                    </div>
                    {noteEditing?.id !== id && (
                      <div className="flex gap-3">
                        {canEditNote && (
                          <button
                            onClick={() => setNoteEditing(currentNote)}
                            disabled={saving}
                            className="pb-2 text-sm"
                          >
                            Edit
                          </button>
                        )}
                        {canEditNote && (
                          <button
                            onClick={() => id && handleDeleteNote(id)}
                            disabled={saving}
                            className="text-danger pb-2 text-sm"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <span>No notes</span>
            )}
          </div>
        </div>
        {/* Pixel Code */}
        {canSeePixelCode && (
          <div className="bg-tint-gray-50 rounded-lg p-4 shadow-sm transition-colors">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-medium">JavaScript Pixel Code</h2>
              <button
                onClick={handleCopyCode}
                className="text-primary hover:text-primary-100"
              >
                <Fa icon="copy" size={1} />
              </button>
            </div>
            <pre
              className="overflow-x-auto rounded-md p-4 text-sm transition-colors"
              style={{
                background: 'var(--toggle-bg)',
                color: 'var(--base-text)',
              }}
            >
              {pixel?.html}
            </pre>
          </div>
        )}
        {/* Pixel History */}
        {pixel?.history && (
          <PixelSubscriptionHistory type="pixel" history={pixel?.history} />
        )}
      </div>
      {/* Active Subscriptions */}
      <div
        className="mx-6 mt-4 mb-6 rounded-lg border p-6 shadow-sm transition-colors"
        style={{
          background: 'var(--base-bg)',
          borderColor: 'var(--base-bg)',
        }}
      >
        <h2 className="mb-4 text-xl font-semibold">Active Subscriptions</h2>
        <Table
          className={classNames('PixelViewPage__SubscriptionTable', {
            'opacity-30': pagination.loading,
          })}
          columns={[
            {
              path: 'name',
              label: 'Subscription Name',
              RowCell: ({ id, name, pixelId }) => (
                <Link
                  to={`/pixel-library/pixel/${pixelId}/subscription/${id}${query}`}
                >
                  {name}
                </Link>
              ),
            },
            // N/A - all are web-to-home for now
            { path: 'type', label: 'Type', RowCell: () => 'Web-to-Home' },
            {
              path: 'created',
              label: 'Date Created',
              RowCell: (row) => {
                if (!row.created) return <EmptyField />
                return formatDate(row.created)
              },
            },
            {
              path: 'id',
              label: 'Subscription ID',
            },
          ]}
          rows={pagination.currentPageData}
        />
        {pagination.loading && !pagination.currentPageData.length && <Loader />}
        {!pagination.currentPageData.length && !pagination.loading ? (
          <div className="PixelViewPage__empty-state flex flex-col items-center py-6">
            <Fa
              icon="folder"
              size={5}
              type="duotone"
              className="text-primary-200"
            />
            <p className="text-grey-600">No Active Subscriptions.</p>
          </div>
        ) : null}
        {pagination.totalItems > 10 ? (
          <div className="PixelViewPage__pagination-footer">
            <TablePagination pagination={pagination} />
          </div>
        ) : null}
      </div>
      {showRejectModal && (
        <PixelRejectionModal
          onClose={() => setShowRejectModal(false)}
          onReject={(rejectionReason) => {
            handleUpdateStatus(
              { label: 'Rejected', value: 'denied' },
              rejectionReason,
            )
          }}
        />
      )}
    </PageContent>
  )
}
